<template lang="pug">
.edit-area
    .page-header-smart-drive
      IQToolMiddlePaneHeader.header(:header="$t(`${baseTranslationPath}.header`)"
                              :title="pageTitle"
                              @goToListPage="navigateAway(false)"
                              )
    .field-container.full-width.is-required(v-if="!isSharePoint")
      label {{ $t(`${baseTranslationPath}.title`) }}
      aiq-input(v-model="record.name"
                :max-length="250")

    .field-container.full-width.is-required.flex-display.img-gap(v-if="showFileInput")
      .file-input
        label {{ recordKind === 'file' ? $t(`${baseTranslationPath}.file`) : $t(`${baseTranslationPath}.folder`) }} {{uploadForbidden ? $t(`${baseTranslationPath}.upload_disallowed`) : ''}}
        .tiny-info {{ tinyInfo }}
        aiq-upload.img-uploader(v-model="imageUrl"
                                :http-request="uploadImage"
                                :action="url"
                                :accept="acceptedFileType"
                                :disabled="uploadForbidden || isSharePoint"
                                :headers="headers"
                                :show-file-list="false"
                                :multiple="recordKind === 'folder'")
          .img-uploader-add
      .file-list
        ViewFiles(v-if="recordKind === 'file'" :record="record" :progress="uploadProgress" :canRemoveFile="isNew" @onDeleteFile="onDeleteFileItem")
        ViewFolder(v-if="recordKind === 'folder' || recordKind === 'sharepoint'" :folderItems="folderItems" :isNew="isNew" @onDeleteFolder="onDeleteFileItem")
    .field-container.full-width.is-required(v-if="['url', 'html', 'urllist'].includes(recordKind)")
      label {{ $t(`${baseTranslationPath}.url`) }}
      aiq-input(v-model="documentUrl"
                :disabled="disableUrl"
                :max-length="250")
    .field-container.full-width.padding-top(v-if="recordKind === 'sftp'")
        label {{ $t(`${baseTranslationPath}.sftp_path`) }}
        aiq-input(v-model="sftpPath"
                  :disabled="disableUrl"
                  :max-length="250")
    .field-container.full-width.padding-top(v-if="hideSegment")
        label {{ $t(`${baseTranslationPath}.teams`) }}
        .select-box-container
          aiq-select(v-model="record.teams"
                    value-key="id"
                    :max-length="250"
                    multiple
                    :reserve-keyword="false")
            aiq-option(v-for="item in teamOptions", :key="item.id", :label="item.name", :value="item")
    .field-container.full-width.padding-top(v-if="hideSegment")
        label {{ $t(`${baseTranslationPath}.segments`) }}
        .select-box-container
          aiq-select(v-model="record.segments"
                    value-key="id"
                    :max-length="250"
                    multiple
                    :reserve-keyword="false")
            aiq-option(v-for="item in segments", :key="item.id", :label="item.name", :value="item")

    .field-container.full-width.flex-display.gap-between(v-if="hideSegment")
      .label-item
        label {{ $t(`${baseTranslationPath}.smart_lynq`) }}
        aiq-tooltip.help-icon(placement="right")
            template(v-slot:content)
              div(v-html="$t(`${baseTranslationPath}.smart_lynq_tooltip`)")
            i.iq-ico-explanation(class="tooltip-help-icon")
      aiq-switch(v-model="upload_faq")
    .field-container.full-width.flex-display.gap-between(v-if="hideSegment")
      label {{ $t(`${baseTranslationPath}.internal_only`) }}
      aiq-tooltip.help-icon(placement="right")
            template(v-slot:content)
              div(v-html="$t(`${baseTranslationPath}.public_tooltip`)")
            i.iq-ico-explanation(class="tooltip-help-icon")
      aiq-switch(v-model="publicCheck" :disabled="!isNew")
    .field-container.full-width.flex-display.gap-between(v-if="showIncludeSubPages")
      label {{ $t(`${baseTranslationPath}.include_subpages`) }}
      aiq-tooltip.help-icon(placement="right")
            template(v-slot:content)
              div(v-html="$t(`${baseTranslationPath}.include_pages_tooltip`)")
            i.iq-ico-explanation
      aiq-switch(v-model="include_subpages" :disabled="disableIncludeSubPages")
    .smart-control(v-if="canViewSmartControl")
      SmartControl(
        :sortedTeams="sortedTeams",
        @rule-satisfied="onRuleSatisfied"
      )
    .foot-area
      .required-label
        span *Required
      .footer
          aiq-button(v-if="hideDelete" type="danger" :disabled="actionInProgress" :plain="true" @click="deleteRecord") {{ deleteInProgress ? $t(`${baseTranslationPath}.delete_in_progress`) : $t(`global.delete`) }}
          aiq-button(@click="navigateAway(false)" :disabled="actionInProgress") {{ $t(`global.cancel`) }}
          aiq-button(type="primary" :disabled="!isSubmittable || actionInProgress" @click="saveRecord") {{ saveInProgress ? $t(`${baseTranslationPath}.save_in_progress`) : $t(`global.save`) }}
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import IQToolMiddlePaneHeader from '@/pages/IQTools/Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import { hosts } from '@/config/api.routes';
import { IMAGE_ACL_TYPES } from '@/constants';
import { confirmBeforeDialogClose } from '@/libs';
import ViewFiles from './ViewFiles.vue';
import ViewFolder from './ViewFolder.vue';
import SmartControl from './SmartControl.vue';
import { RECORD_KINDS } from './smartDriveUtil';
import path from 'path-browserify';
const COMPLETE_UPLOAD = 100;
const WAIT_TIME = 700;

export default {
  name: 'smart-drive-edit',
  components: {
    ViewFiles,
    ViewFolder,
    SmartControl,
    IQToolMiddlePaneHeader,
  },
  data() {
    return {
      isNew: false,
      imageUrl: '',
      uploadProgress: {},
      publicCheck: false,
      upload_faq: false,
      itemsToUpload: [],
      disableUpload: false,
      relatedRecords: [],
      timeoutId: null,
      include_subpages: false,
      isAppendItem: false,
      hidePermission: false,
      folderItems: [],
      smartControlPayload: null,
      smartControlSatisfied: true,
      smartControlVersionSatisfied: true,
      url: `${hosts.management}files/upload`,
      headers: {
        Authorization: this.$store.getters['agent/authToken'],
      },
      documentUrl: '',
      sftpPath: '',
      record: {
        name: '',
        description: '',
        file: {
          payload: {},
        },
        url: '',
        teams: [],
        path: '',
        segments: [],
        acl: IMAGE_ACL_TYPES.PUBLIC,
      },
      originalRecord: {},
      originalCheck: {},
      recordKind: '',
      fetchAction: 'documents/getDocumentV2',
      editAction: 'documents/updateDocumentV2',
      createAction: 'documents/createDocumentV2',
      deleteAction: 'documents/deleteDocumentV2',
      visible: true,
      baseTranslationPath: 'settings_tab.smart_drive_tab.edit',
      saveInProgress: false,
      deleteInProgress: false,
    };
  },
  computed: {
    ...mapState({
      agentProfile: (state) => state.agent.profile,
      FEATURE_FLAGS: state => state.featureFlags.FEATURE_FLAGS,
    }),
    ...mapGetters({
      sortedTeams: 'teams/sortedTeams',
      segments: 'configs/segments',
    }),

    pageTitle() {

      if (this.recordKind === RECORD_KINDS.FILE) {
        return this.$t(`${this.baseTranslationPath}.manage_file`);
      }

      if (this.recordKind === RECORD_KINDS.FOLDER || this.recordKind === RECORD_KINDS.ADDITONALFOLDER) {
        return this.$t(`${this.baseTranslationPath}.manage_folder`);
      }

      if (this.recordKind === RECORD_KINDS.SFTP) {
        return this.$t(`${this.baseTranslationPath}.manage_sftp`);
      }

      if (this.recordKind === RECORD_KINDS.SHAREPOINT) {
        return this.$t(`${this.baseTranslationPath}.manage_sharepoint`);
      }

      return this.$t(`${this.baseTranslationPath}.manage_link`);

    },
    actionInProgress() {
      return this.saveInProgress || this.deleteInProgress;
    },

    isSubmittable() {
      const record_changed = (!isEqual(this.originalRecord, this.record) || this.publicCheck !== this.originalCheck.publicCheck ||
      this.upload_faq !== this.originalCheck.upload_faq ||
      this.include_subpages !== this.originalCheck.include_subpages);
      if (this.recordKind === RECORD_KINDS.FILE) {
        return this.getFiles().length > 0 && !!this.getFiles()[0].id && !!this.record.name && record_changed;
      }
      if (this.recordKind === RECORD_KINDS.URL || this.recordKind === RECORD_KINDS.HTML) {
        return !!this.documentUrl && !!this.record.name && record_changed;
      }
      if (this.recordKind === RECORD_KINDS.SFTP) {
        return !!this.sftpPath && !!this.record.name && record_changed;
      }
      return !!this.record.name && record_changed;
    },
    canViewSmartControl() {
      const smart_control_feat_flag = this.FEATURE_FLAGS.SHOW_SMART_CONTROL;
      const has_acl = this.canView('/settings/smartdrive/rules/assign');
      const isFile = this.recordKind === RECORD_KINDS.FILE;
      return smart_control_feat_flag && has_acl && isFile && this.isNew;
    },
    hideSegment() {
      return !(this.isAppendItem || this.hidePermission);
    },
    disableIncludeSubPages() {
      return !this.upload_faq || !this.FEATURE_FLAGS.GENERATIVE_FAQ;
    },
    hideDelete() {
      if (this.isAppendItem) {
        return false;
      }
      return !this.isNew;
    },
    showIncludeSubPages() {
      if (this.isAppendItem && (this.recordKind === RECORD_KINDS.URL || this.recordKind === RECORD_KINDS.HTML)) {
        return true;
      }
      if (this.hidePermission) {
        return false;
      }
      if (!this.isNew) {
        return false;
      }
      return (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML
      );
    },
    acceptedFileType() {
      if (this.recordKind === RECORD_KINDS.FILE) {
        if (this.FEATURE_FLAGS.ENABLE_SMARTDRIVE_IMAGE) {
          return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,image/*';
        } else {
          return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv';
        }
      } else {
        return '*/*';
      }
    },
    isSharePoint() {
      const sharepointUrl = get(this.record, 'meta.sharepoint.url', '');
      const sharepointId = get(this.record, 'meta.sharepoint.id', '');
      if (sharepointId.length > 0 || sharepointUrl.length > 0) {
        return true;
      }
      return false;
    },
    uploadForbidden() {
      if (this.isAppendItem) {
        return false;
      }

      if (this.recordKind === RECORD_KINDS.FILE && !this.isNew) {
        return true;
      }

      return this.recordKind === RECORD_KINDS.FOLDER && !this.isNew;
    },
    hasSelectedItem() {
      return this.itemsToUpload.length > 0;
    },
    disableUrl() {
      if (this.isAppendItem) {
        return false;
      }
      return !this.isNew;
    },
    showFileInput() {
      if (this.hidePermission) {
        return false;
      }
      if (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML ||
        this.recordKind === RECORD_KINDS.SFTP
      ) {
        return false;
      }

      if (
        this.recordKind === RECORD_KINDS.ADDITONALFOLDER &&
        this.isAppendItem
      ) {
        return false;
      }
      if (this.isAppendItem) {
        return true;
      }

      if (this.recordKind === RECORD_KINDS.FOLDER && !this.isNew) {
        return false;
      }

      if (this.recordKind === RECORD_KINDS.FOLDER) {
        return true;
      }

      if (this.recordKind === RECORD_KINDS.FILE) {
        return true;
      }

      return false;
    },
    tinyInfo() {
      if (this.isSharePoint) {
        return this.$t(
          `${this.baseTranslationPath}.upload_to_sharepoint_forbidden`,
        );
      }
      return '';
    },

    isPublic() {
      if (!this.isNew) {
        return (
          get(this.record, 'file.payload.acl', 'private') !==
          IMAGE_ACL_TYPES.PUBLIC
        );
      }
      return this.publicCheck;
    },
    teamOptions() {
      return this.canView('/users/filters/show_all_teams')
        ? this.sortedTeams
        : this.agentProfile.teams;
    },
  },
  /**
   * Vue lifecycle hook
   * Add webkitdirectory attribute to the input element to enable folder upload
   * whenever the component is updated and recordKind is folder.
   */
  updated() {
    if (this.recordKind === 'folder') {
      const inputElement = document.querySelector('.el-upload__input');
      if (!inputElement) return;
      if (!inputElement.hasAttribute('webkitdirectory')) {
        inputElement.setAttribute('webkitdirectory', true);
        inputElement.setAttribute('multiple', true);
        inputElement.setAttribute('directory', true);
      }
    }
  },
  async mounted() {
    await this.initialize();
    if (!this.record.segments) {
      this.record.segments = [];
    }
    this.sftpPath = get(this.record, 'meta.path', '');
  },
  methods: {
    async initialize() {
      const params = get(this.$route, 'params.id', '');
      const query = get(this.$route, 'query.type', '');
      this.hidePermission = get(this.$route, 'query.hidePermission', false);
      this.isNew = params === 'new';
      this.recordKind =
        Object.values(RECORD_KINDS).find((kind) => query === kind) || '';
      this.isAppendItem = get(this.$route, 'query.appendItem', false);
      if (this.recordKind === '') {
        this.navigateAway(true);
      }

      if (!this.isNew) {
        this.record = (
          await this.$store.dispatch(this.fetchAction, [
            params,
            this.teamOptions.map((x) => x.id),
          ])
        ).data;
        this.upload_faq = get(this.record, 'meta.genai', false);
        let acl;
        if (get(this.record, 'object_type') === RECORD_KINDS.FOLDER ||
          get(this.record, 'object_type') === RECORD_KINDS.URL ||
          get(this.record, 'object_type') === RECORD_KINDS.HTML ||
          get(this.record, 'object_type') === RECORD_KINDS.SHAREPOINT ||
          get(this.record, 'object_type') === RECORD_KINDS.URLLIST
        ) {
          acl = get(this.record, 'meta.acl', 'private');
        } else {
          acl = get(this.record, 'file.payload.acl', 'private');
        }
        this.publicCheck = acl === IMAGE_ACL_TYPES.PUBLIC ? true : false;
        //remove duplicate teams
        this.record.teams = this.record.teams.filter(
          (team, index) =>
            this.record.teams.findIndex((t) => t.id === team.id) === index,
        );
        if (
          this.recordKind === RECORD_KINDS.URL ||
          this.recordKind === RECORD_KINDS.HTML ||
          this.recordKind === RECORD_KINDS.URLLIST
        ) {
          this.include_subpages = get(this.record, 'meta.genai', false);
          if (get(this.record, 'file.payload.url')) {
            this.documentUrl = get(this.record, 'file.payload.url');
          } else {
            this.documentUrl = get(this.record, 'meta.url', '');
          }
        }
      }
      if (this.isAppendItem) {
        this.record.name = '';
      }
      this.originalRecord = cloneDeep(this.record);
      this.originalCheck = {
        publicCheck: this.publicCheck,
        include_subpages: this.include_subpages,
        upload_faq: this.upload_faq,
      };
      const isNewUpload = get(this.record, 'file.payload.name', '') === '';
      if (!isNewUpload) {
        this.uploadProgress = {
          uploadPercent: COMPLETE_UPLOAD,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },
    onRuleSatisfied(data) {
      this.smartControlPayload = data.selectedRule;
      this.smartControlSatisfied = data.isRuleSatisfied;
      this.smartControlVersionSatisfied = data.versionSatisfied;
    },

    async onDeleteFileItem(payload) {
      try {
        if (payload.type === 'file') {
          await this.$store.dispatch('files/deleteFile', payload.file.id);
          this.record.file = {};
        } else {
          await this.$store.dispatch('files/deleteFile', payload.file.id);
          //remove from this.folderList
          this.folderItems = this.folderItems.filter(
            (item) => item.id !== payload.file.id,
          );
        }
      } catch (e) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.unable_to_delete`),
        );
      }
    },
    closeEditor() {
      this.visible = false;
    },
    getFiles() {
      if (this.recordKind === RECORD_KINDS.URL) {
        return [];
      }
      if (this.recordKind === RECORD_KINDS.FILE) {
        if (this.record.file) {
          return [
            {
              id: this.record.file.id,
              path: this.record.file.filename,
            },
          ];
        } else {
          return [];
        }
      }
      if (this.recordKind === RECORD_KINDS.FOLDER) {
        return this.folderItems.map((item) => {
          return {
            id: item.id,
            path: item.payload.webkitRelativePath,
          };
        });
      }
      return [];
    },
    getTeams() {
      return this.record.teams.map((team) => team.id);
    },
    getSegments() {
      return this.record.segments.map((segment) => segment.id);
    },
    async saveRecord() {
      let data;
      let parent_id;
      if (this.isAppendItem) {
        parent_id = parseInt(get(this.$route, 'params.id', ''));
      } else {
        parent_id = get(this.record, 'parent_id');
      }

      if (!this.smartControlSatisfied) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.smart_control_error`),
        );
        return;
      }

      if (!this.smartControlVersionSatisfied) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.smart_control_version_error`),
        );
        return;
      }

      if (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML ||
        this.recordKind === RECORD_KINDS.SFTP
      ) {
        data = {
          type: this.recordKind,
          title: this.record.name,
          url: this.documentUrl,
          description: this.record.description || '',
          permissions: this.getTeams().length > 0 ? true : false,
          acl: this.publicCheck ? IMAGE_ACL_TYPES.PUBLIC : IMAGE_ACL_TYPES.PRIVATE,
          teams: this.getTeams(),
          segments: this.getSegments(),
          path: this.sftpPath,
          upload_faq: this.upload_faq,
          ...(this.recordKind === RECORD_KINDS.SFTP ? {} : {
            include_subpages: this.include_subpages,
          }),
          ...(parent_id ? { parent_id } : {}),
        };
      } else {
        data = {
          files: this.getFiles(),
          type:
            this.recordKind === RECORD_KINDS.ADDITONALFOLDER
              ? RECORD_KINDS.FOLDER
              : this.recordKind,
          title: this.record.name,
          url: this.documentUrl,
          description: this.record.description || '',
          permissions: this.getTeams().length > 0 ? true : false,
          segments: this.getSegments(),
          acl: this.publicCheck ? IMAGE_ACL_TYPES.PUBLIC : IMAGE_ACL_TYPES.PRIVATE,
          teams: this.getTeams(),
          upload_faq: this.upload_faq,
          ...(parent_id ? { parent_id } : {}),
          ...(this.smartControlPayload !== null ? {
            smart_control: this.smartControlPayload,
          } : {}),
        };
      }

      try {
        this.saveInProgress = true;
        if (!this.isNew && !this.isAppendItem) {
          const payload = {
            id: this.record.id,
            payload: data,
          };
          await this.$store.dispatch(this.editAction, payload);
          this.$aiq.notify.success(
            this.$t(`${this.baseTranslationPath}.saved_successfully`),
          );
          this.navigateAway(true);
        } else {
          const payload = data;
          await this.$store.dispatch(this.createAction, payload);
          this.$aiq.notify.success(
            this.$t(`${this.baseTranslationPath}.saved_successfully`),
          );
          this.navigateAway(true);
        }
      } catch (e) {
        const errorMessage = get(
          e,
          'response.data.message',
          this.$t(`${this.baseTranslationPath}.unable_to_save`),
        );
        this.$aiq.notify.error(errorMessage);
      } finally {
        this.saveInProgress = false;
      }
    },

    onUploadProgress(progressEvent) {
      const percent = Math.round(
        (progressEvent.loaded * COMPLETE_UPLOAD) / progressEvent.total,
      );
      if (percent !== COMPLETE_UPLOAD) {
        this.uploadProgress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'in progress',
        };
      } else {
        this.uploadProgress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },

    folderUploadProgress(progressEvent) {
      const percent = Math.round(
        (progressEvent.loaded * COMPLETE_UPLOAD) / progressEvent.total,
      );
      if (percent !== COMPLETE_UPLOAD) {
        this.folderItems[0].progress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'in progress',
        };
      } else {
        this.folderItems[0].progress = {
          uploadPercent: percent,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },
    async uploadFolderList() {
      if (this.itemsToUpload.length < 1) {
        this.record.title = '';
        return;
      }
      const item = this.itemsToUpload[this.itemsToUpload.length - 1];
      if (this.record.name === '') {
        this.record.name = path.dirname(item.webkitRelativePath);
      }

      for (const data of this.itemsToUpload) {
        const formData = new FormData();
        formData.append('file', data);
        formData.append('acl', IMAGE_ACL_TYPES.PUBLIC);
        if (!this.FEATURE_FLAGS.ENABLE_SMARTDRIVE_IMAGE) {
          if (
            data.type.includes('video/') ||
          data.type.includes('audio/') ||
          data.type.includes('image/')
          ) {
          //file type not supported in folder upload
            continue;
          }
        }

        this.folderItems = [
          {
            payload: {
              name: data.name,
              url: '',
              mime: data.type,
            },
            progress: {
              uploadPercent: 0,
              uploadStatus: 'success',
              progressBarStatus: 'in progress',
            },
          },
          ...this.folderItems,
        ];
        try {
          const response = await this.$store.dispatch('files/upload', {
            formData,
            url: this.url,
            onUploadProgress: this.folderUploadProgress,
          });

          this.folderItems[0] = {
            id: response.data.file_id,
            payload: {
              name: response.data.name,
              url: response.data.url,
              mime: response.data.mime,
              webkitRelativePath: data.webkitRelativePath,
            },
            progress: {
              uploadPercent: COMPLETE_UPLOAD,
              uploadStatus: 'success',
              progressBarStatus: 'complete',
            },
          };
        } catch (error) {
          // handle error
          this.folderItems[0].progress = {
            uploadPercent: COMPLETE_UPLOAD,
            uploadStatus: 'exception',
            progressBarStatus: 'failure',
          };
        }
      }
      this.disableUpload = true;
    },
    async processBulkUpload() {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        if (this.itemsToUpload.length > 0) {
          this.uploadFolderList();
        }
      }, WAIT_TIME);
    },
    async uploadImage(data) {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('acl', IMAGE_ACL_TYPES.PUBLIC);
      //on folder upload, aiq-upload fires multiple instances of this function
      //so we need to handle it differently
      //save all files to itemsToUpload  temporary array
      //and upload them one by one
      if (this.recordKind === 'file') {
        if (this.record.name === '') {
          this.record.name = data.file.name;
        }
        //temporary file list
        this.record = {
          ...this.record,
          file: {
            payload: {
              name: data.file.name,
              mime: data.file.type,
              url: '',
            },
          },
        };
      } else {
        this.itemsToUpload = [data.file, ...this.itemsToUpload];
      }

      try {
        if (this.recordKind === RECORD_KINDS.FILE) {
          const response = await this.$store.dispatch('files/upload', {
            formData,
            url: this.url,
            onUploadProgress: this.onUploadProgress,
          });

          this.record = {
            ...this.record,
            file: {
              payload: { ...response.data },
              id: response.data.file_id,
            },
          };
        }
        if (this.recordKind === RECORD_KINDS.FOLDER) {
          await this.processBulkUpload();
        }
      } catch (error) {
        // handle error
        this.uploadProgress = {
          uploadPercent: COMPLETE_UPLOAD,
          uploadStatus: 'exception',
          progressBarStatus: 'failure',
        };
      }
    },
    async deleteConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.delete_confirm.title`),
          this.$t(`${this.baseTranslationPath}.delete_confirm.message`),
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteRecord() {
      try {
        const confirm = await this.deleteConfirm();
        if (!confirm) {
          return;
        }

        this.deleteInProgress = true;
        await this.$store.dispatch(this.deleteAction, this.record.id);
        this.$aiq.notify.success(
          this.$t(`${this.baseTranslationPath}.deleted_successfully`),
        );
        this.navigateAway(true);
      } catch (err) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.unable_to_delete`),
        );
      } finally {
        this.deleteInProgress = false;
      }
    },
    handleBeforeClose() {
      if (!isEqual(this.originalRecord, this.record)) {
        confirmBeforeDialogClose(this, this.closeEditor);
      } else if (this.publicCheck !== this.originalCheck.publicCheck ||
      this.upload_faq !== this.originalCheck.upload_faq ||
      this.include_subpages !== this.originalCheck.include_subpages) {
        confirmBeforeDialogClose(this, this.closeEditor);
      }
      else {
        this.closeEditor();
      }
    },

    async validateForChanges() {
      if (isEqual(this.originalRecord, this.record)) {
        return true;
      } else {
        try {
          await this.$aiq.confirm(
            this.$t('modal_editor.before_close_confirm.title'),
            this.$t('modal_editor.before_close_confirm.message', {
              name: this.record.name,
              defaultValue: 'modal_editor.before_close_confirm.message',
            }),
          );
          return true;
        } catch {
          return false;
        }
      }
    },
    async navigateAway(val = false) {
      if (!val) {
        const changes = await this.validateForChanges();
        if (!changes) {
          return;
        }
      }
      // editor is contained by a route
      const id = get(this, '$route.params.id');
      if (id && this.$route.matched.length > 1) {
        // get the parent path
        const currentPath = this.$route.path;
        const parentPath = currentPath.substring(
          0,
          currentPath.lastIndexOf('/'),
        );
        // get parant name if exist
        const name = get(this.$route, 'meta.parentName');
        // move to the parent
        this.$router.push(name ? { name } : parentPath);
      }
    },
    getTitle() {
      if (!this.isNew) {
        return this.$t(`${this.baseTranslationPath}.edit_${this.recordKind}`);
      } else {
        return this.$t(`${this.baseTranslationPath}.upload_${this.recordKind}`);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.edit-area {
  margin-top: 20px;
  height: calc(100vh - 150px);
  overflow-y: auto;
}
.field-container {
  margin-top: 10px;
}

.file-input {
  flex: 1;
}
.required-label {
  display: flex;
  justify-content: flex-end;
  color: red;
}

.foot-area {
  margin-top: 20px;
}
.footer {
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.flex-display {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.file-list {
  margin-left: auto;
}

.file-list {
  margin-top: 20px;
}

.padding-top {
  padding-top: 10px;
}

.tiny-info {
  color: #475669;
  font-size: 10px;
  font-weight: 80;
}
.gap-between {
  padding-top: 20px;
  gap: 10px;
}

.tooltip-help-icon {
  padding-left: 5px;
}

:deep(.el-upload) {
  position: relative;
  width: 100%;
  height: 100%;
}

.img-gap {
  gap: 20%;
}

.img-uploader {
    width: 400px;
    height: 200px;
    border-radius: 4px;
    border: 1px dashed #BFCBD9;
    background-color: #F9FAFC;
    overflow: hidden;

    &:hover {
      border-color: #20a0ff;
    }

  }

.img-uploader-preview {
    width: 100%;
    height: 100%;
    background-position: center center;
    background-size: cover;
}

.img-uploader-container {
    margin-bottom: 10px;
    color: #475669;
    position: absolute;
    width: 130px;
    font-size: 14px;
    font-weight: 100;

    label {
      display: block;
      margin-bottom: 10px;
      color: #475669;
      font-size: 14px;
      font-weight: 100;
      text-align: left;
    }
  }
.img-uploader-add {
    position: relative;
    width: 100%;
    height: 100%;

    &::before {
      position: absolute;
      top: 50%;
      left: 50%;
      display: block;
      width: 28px;
      height: 2px;
      content: '';
      border-radius: 2px;
      transform: translate(-50%, -50%);
      background-color: #BFCBD9;
    }
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      display: block;
      width: 28px;
      height: 2px;
      content: '';
      border-radius: 2px;
      transform: translate(-50%, -50%) rotate(90deg);
      background-color: #BFCBD9;
    }
  }
:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #5993FF !important;
  border-color: #5993FF !important;
}
</style>
